import { array, object, string, number } from 'yup';

import getValidationTexts from 'common/utils/getValidationTexts';
import { checkMinMaxObjectKeyValues } from 'common/utils/helpers';

export const buildOpexFields = () => {
    const { getMinValueText, INVALID_TEXT, NUMBER_TYPE_TEXT, REQUIRED_TEXT } =
        getValidationTexts();

    const schema = {
        opex: number().when('opex_active', {
            is: true,
            then: () =>
                number()
                    .typeError(NUMBER_TYPE_TEXT)
                    .required(REQUIRED_TEXT)
                    .min(0, getMinValueText(0))
                    .nullable(),
            otherwise: () => string().nullable(),
        }),
        opex_cost_ranges_kw: array().when('opex_costing_type', {
            is: 'by_kw_and_kwh',
            then: () =>
                array()
                    .of(
                        object().shape({
                            amount: number()
                                .typeError(REQUIRED_TEXT)
                                .required(REQUIRED_TEXT),
                            min: number().typeError(REQUIRED_TEXT).nullable(),
                            max: number()
                                .typeError(REQUIRED_TEXT)
                                .test('min', INVALID_TEXT, function (value) {
                                    const { min } = this.parent;
                                    if (!min && min !== 0) return true;
                                    return value >= min;
                                })
                                .nullable(),
                        }),
                    )
                    .min(1, 'Cost ranges watt must have at least one element')
                    .required('Cost ranges watt is required'),
        }),
        opex_costing_type: string().when('opex_active', {
            is: true,
            then: () => string().required(REQUIRED_TEXT).nullable(),
            otherwise: () => string().nullable(),
        }),
        opex_percentage_increase: number().when('opex_active', {
            is: true,
            then: () =>
                number()
                    .typeError(NUMBER_TYPE_TEXT)
                    .required(REQUIRED_TEXT)
                    .min(0, getMinValueText(0))
                    .nullable(),
            otherwise: () => string().nullable(),
        }),
    };

    return schema;
};

export const buildFields = () => {
    const {
        getMaxValueText,
        getMinValueText,
        INVALID_TEXT,
        MAX_100_VALIDATION_TEXT,
        MIN_ZERO_VALIDATION_TEXT,
        NUMBER_TYPE_TEXT,
        REQUIRED_TEXT,
    } = getValidationTexts();

    const opexFields = buildOpexFields();

    return {
        financial_work_cost_month: number().when('work_cost_active', {
            is: true,
            then: () =>
                number()
                    .typeError(NUMBER_TYPE_TEXT)
                    .required(REQUIRED_TEXT)
                    .min(0, getMinValueText(0))
                    .nullable(),
            otherwise: () => string().nullable(),
        }),
        name: string().required(REQUIRED_TEXT).nullable(),
        parameters: {
            tax: number()
                .typeError(NUMBER_TYPE_TEXT)
                .min(0, MIN_ZERO_VALIDATION_TEXT)
                .max(100, MAX_100_VALIDATION_TEXT)
                .nullable(),
            terms: array()
                .test(
                    'terms-range',
                    getMaxValueText('360') + ', ' + getMinValueText('1'),
                    (value) => checkMinMaxObjectKeyValues(value, 1, 360),
                )
                .nullable(),
        },
        parameters_commission_credit: object().shape({
            unit: number().typeError(NUMBER_TYPE_TEXT).nullable(),
            value: number().when('unit', {
                is: 0,
                then: () =>
                    number()
                        .typeError(NUMBER_TYPE_TEXT)
                        .min(0, MIN_ZERO_VALIDATION_TEXT)
                        .max(100, MAX_100_VALIDATION_TEXT)
                        .nullable(),
            }),
        }),
        parameters_hitch: object().shape({
            unit: number().typeError(NUMBER_TYPE_TEXT).nullable(),
            value: number().when('unit', {
                is: 0,
                then: () =>
                    number()
                        .typeError(NUMBER_TYPE_TEXT)
                        .min(0, MIN_ZERO_VALIDATION_TEXT)
                        .max(100, MAX_100_VALIDATION_TEXT)
                        .nullable(),
            }),
        }),
        parameters_rate: object().shape({
            value: number()
                .typeError(NUMBER_TYPE_TEXT)
                .min(0, MIN_ZERO_VALIDATION_TEXT)
                .max(100, MAX_100_VALIDATION_TEXT)
                .nullable(),
        }),
        work_cost_ranges: array().when('work_cost_active', {
            is: true,
            then: () =>
                array()
                    .of(
                        object().shape({
                            min: number().typeError(REQUIRED_TEXT).nullable(),
                            max: number()
                                .typeError(REQUIRED_TEXT)
                                .test('min', INVALID_TEXT, function (value) {
                                    const { min } = this.parent;
                                    if (!min && min !== 0) return true;
                                    return value >= min;
                                })
                                .nullable(),
                            month: number()
                                .typeError(REQUIRED_TEXT)
                                .required(REQUIRED_TEXT),
                        }),
                    )
                    .min(1, 'Cost ranges watt must have at least one element')
                    .required('Cost ranges watt is required'),
        }),

        ...opexFields,
    };
};
